import { useEffect, useState } from 'react';
import {
    Button,
    SimpleForm,
    Toolbar,
    SaveButton,
    useNotify,
    ArrayInput,
    SimpleFormIterator,
    required,
} from 'react-admin';
import { Autocomplete, TextField, CircularProgress, Box } from '@mui/material';
import { apiRoutes } from '../../../services/apiRoutes';
import { goApi } from '../../../services';
import { useController } from 'react-hook-form';

export const SubjectAutocompleteWithScroll = ({ source, templateId }: { source: string, templateId: string }) => {
    const { field } = useController({ name: source });
    const [subjects, setSubjects] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const notify = useNotify();

    const fetchSubjects = async (pageNum: number, search: string = '') => {
        if (!templateId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                batchTemplateId: templateId,
                limit: '20',
                page: pageNum.toString(),
                ...(search && { name: search }),
            });
            const response = await goApi.get(
                `${apiRoutes.BATCH_TEMPLATE_SUBJECTS}?${params.toString()}`,
            );
            const newSubjects = response.data?.items || [];

            if (pageNum === 1) {
                setSubjects(newSubjects);
            } else {
                setSubjects(prev => [...prev, ...newSubjects]);
            }

            setHasMore(newSubjects.length === 20);
        } catch (err) {
            notify('Failed to fetch subjects', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
        setSubjects([]);
        fetchSubjects(1, searchText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId, searchText]);

    const handleScroll = (event: any) => {
        const listboxNode = event.currentTarget;
        const position = listboxNode.scrollTop + listboxNode.clientHeight;
        if (listboxNode.scrollHeight - position <= 1 && hasMore && !loading) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchSubjects(nextPage, searchText);
        }
    };

    return (
        <Autocomplete
            options={subjects}
            fullWidth
            getOptionLabel={(option: any) => option.name || option.title || ''}
            getOptionKey={(option: any) => option.subjectId}
            value={subjects.find((s: any) => s.subjectId === field.value) || null}
            onChange={(_, newValue) => {
                const newId = newValue?.subjectId || '';
                field.onChange(newId);
            }}
            onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                    setSearchText(value);
                }
            }}
            loading={loading}
            ListboxProps={{
                onScroll: handleScroll,
                style: { maxHeight: '250px' },
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label="Subject"
                    required
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export const BatchesAutocompleteWithScroll = ({ source, onBatchChange }: { source: string, onBatchChange: (templateId: string) => void }) => {
    const { field } = useController({ name: source });
    const [batches, setBatches] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const notify = useNotify();

    const fetchBatches = async (pageNum: number, search: string = '') => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limit: '20',
                page: pageNum.toString(),
                ...(search && { name: search }),
            });
            const response = await goApi.get(`/batches?${params.toString()}`);
            const newBatches = response.data?.items || [];

            if (pageNum === 1) {
                setBatches(newBatches);
            } else {
                setBatches(prev => [...prev, ...newBatches]);
            }

            setHasMore(newBatches.length === 20);
        } catch (err) {
            notify('Failed to fetch primary teachers', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setPage(1);
        setBatches([]);
        fetchBatches(1, searchText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText]);

    const handleScroll = (event: any) => {
        const listboxNode = event.currentTarget;
        const position = listboxNode.scrollTop + listboxNode.clientHeight;
        if (listboxNode.scrollHeight - position <= 1 && hasMore && !loading) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchBatches(nextPage, searchText);
        }
    };

    return (
        <Autocomplete
            fullWidth
            options={batches}
            getOptionLabel={(option: any) => {
                return `${option?.title || ''}`;
            }}
            value={batches.find((t: any) => t.id === field.value) || null}
            onChange={(_, newValue) => {
                field.onChange(newValue?.id || '');
                onBatchChange(newValue?.templateId || '');
            }}
            onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                    setSearchText(value);
                }
            }}
            loading={loading}
            ListboxProps={{
                onScroll: handleScroll,
                style: { maxHeight: '250px' },
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    label="Select Batch"
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

const BatchSubjectPair = ({ source, index }: { source: string, index: number }) => {
    const [templateId, setTemplateId] = useState('');
    return (
        <Box sx={{ display: 'flex', gap: 2, width: '100%', alignItems: 'center', mb: 1 }}>
            <BatchesAutocompleteWithScroll 
                source={`${source}.batchId`} 
                onBatchChange={(tid) => setTemplateId(tid)} 
            />
            <SubjectAutocompleteWithScroll source={`${source}.subjectId`}  templateId={templateId} />
        </Box>
    );
};

export const DuplicateScheduleForm = ({
    batchId,
    schedule,
    onClose,
}: any) => {
    const notify = useNotify();

    const postSave = async (data: any) => {
        if (!data.targets || data.targets.length === 0) {
            notify('Please add at least one batch and subject', { type: 'warning' });
            return;
        }

        try {
            const targets = data.targets.map((t: any) => ({
                batchId: t.batchId,
                subjectId: t.subjectId,
            }));
           
            goApi.post(`/batches/schedules/${schedule.id}/duplicate`, {targets})
           
            notify('Schedules copied successfully', { type: 'success' });
            onClose?.(true);
        } catch (err: any) {
            notify(err?.message || `Failed to copy schedule`, {
                type: 'error',
            });
        }
    };

    return (
        <div className="w-[500px] max-w-[90vw]">
            <p className="sticky top-0 p-2 text-lg font-semibold bg-white z-[1000] border-b border-gray-200">
                Duplicate Schedule to Multiple Batches
            </p>
            <>
                <SimpleForm
                    onSubmit={postSave}
                    record={{}}
                    defaultValues={{ targets: [{ batchId: '', subjectId: '' }] }}
                    toolbar={
                        <Toolbar
                            className="flex justify-between"
                            sx={{
                                position: 'sticky',
                                bottom: 0,
                                backgroundColor: 'white',
                                zIndex: 1000,
                                borderTop: '1px solid #e0e0e0',
                            }}
                        >
                            <SaveButton label='Copy' />
                            <Button
                                variant="outlined"
                                color="error"
                                label="Cancel"
                                onClick={() => onClose?.(false)}
                            />
                        </Toolbar>
                    }
                >
                    <ArrayInput source="targets" label="Target Batches & Subjects" validate={[required()]}>
                        <SimpleFormIterator inline fullWidth>
                            <BatchSubjectPair source="" index={0} />
                        </SimpleFormIterator>
                    </ArrayInput>
                </SimpleForm>
            </>
        </div>
    );
};
